import React, { useState, useRef, useMemo, useCallback, memo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import MapGL, { 
  GeolocateControl, 
  NavigationControl, 
  Marker
} from 'react-map-gl';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CalendarIcon,
  MapPinIcon,
  ClockIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { REGION_CONFIG } from '../config/region';
import Header from '../components/Common/Header';
import { 
  ETC1_CONFIG, 
  ETC2_CONFIG, 
  EVENT_TYPE_SLUGS,
  getEventTypeConfig,
  getEventTypeDisplayName, getETC1Config, getETC2Config } from '../config/eventTypeSchema';
import 'mapbox-gl/dist/mapbox-gl.css';
import { API_BASE_URL } from '../utils/constants';
import { dtLocale } from '../utils/dateLocale';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const DESKTOP_BREAKPOINT = 1024;

// Desktop detection hook
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= DESKTOP_BREAKPOINT);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isDesktop;
};

// Date range types
const DATE_RANGES = {
  LAST_7: 'last7',
  NOW: 'now',
  NEXT_7: 'next7',
  NEXT_14: 'next14',
  CUSTOM: 'custom'
};

// Time of day filters
const TIME_OF_DAY = {
  ALL: 'all',
  MORNING: 'morning',
  AFTERNOON: 'afternoon',
  EVENING: 'evening'
};

// Check if event occurs on a specific date
const eventOccursOnDate = (event, targetDate) => {
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);
  const targetTime = target.getTime();
  const targetWeekday = WEEKDAY_CODES[target.getDay()];
  const targetMonthday = target.getDate();
  
  if (event.isActive === false) return false;
  
  const eventStart = event.eventStartsAt || event.seriesStartDate;
  const eventEnd = event.eventEndsAt || event.seriesEndDate;
  if (!eventStart) return false;
  
  const startDate = new Date(eventStart);
  startDate.setHours(0, 0, 0, 0);
  const endDate = eventEnd ? new Date(eventEnd) : null;
  if (endDate) endDate.setHours(23, 59, 59, 999);
  
  if (targetTime < startDate.getTime()) return false;
  if (endDate && targetTime > endDate.getTime()) return false;
  
  const recurrence = event.recurrenceFreq || event.eventRecurrence || 'NONE';
  if (recurrence === 'NONE' || event.scheduleMode === 'ONE_OFF') return true;
  
  if (event.exceptionsCancelledDates?.length > 0) {
    const isCancelled = event.exceptionsCancelledDates.some(d => {
      const cancelled = new Date(d);
      cancelled.setHours(0, 0, 0, 0);
      return cancelled.getTime() === targetTime;
    });
    if (isCancelled) return false;
  }
  
  if (event.seriesIsActive === false) return false;
  const interval = event.recurrenceInterval || 1;
  
  if (recurrence === 'DAILY') {
    if (interval === 1) return true;
    const daysDiff = Math.floor((targetTime - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysDiff % interval === 0;
  }
  
  if (recurrence === 'WEEKLY') {
    const weekdays = event.byWeekday || event.daysOfWeek?.map(d => WEEKDAY_CODES[d]) || [];
    if (weekdays.length === 0) {
      if (targetWeekday !== WEEKDAY_CODES[startDate.getDay()]) return false;
    } else if (!weekdays.includes(targetWeekday)) return false;
    if (interval > 1) {
      const weeksDiff = Math.floor((targetTime - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7));
      if (weeksDiff % interval !== 0) return false;
    }
    return true;
  }
  
  if (recurrence === 'MONTHLY') {
    const monthdays = event.byMonthday || [];
    if (monthdays.length === 0) {
      if (targetMonthday !== startDate.getDate()) return false;
    } else if (!monthdays.includes(targetMonthday)) return false;
    if (interval > 1) {
      const monthsDiff = (target.getFullYear() - startDate.getFullYear()) * 12 + (target.getMonth() - startDate.getMonth());
      if (monthsDiff % interval !== 0) return false;
    }
    return true;
  }
  
  return true;
};

// Check if event matches time of day filter
const eventMatchesTimeOfDay = (event, timeFilter) => {
  if (timeFilter === TIME_OF_DAY.ALL) return true;
  
  let eventHour = null;
  
  if (event.timeSlots?.length > 0) {
    const [startH] = event.timeSlots[0].start.split(':').map(Number);
    eventHour = startH;
  } else if (event.eventStartsAt) {
    eventHour = new Date(event.eventStartsAt).getHours();
  }
  
  if (eventHour === null) return true;
  
  switch (timeFilter) {
    case TIME_OF_DAY.MORNING:
      return eventHour >= 6 && eventHour < 12;
    case TIME_OF_DAY.AFTERNOON:
      return eventHour >= 12 && eventHour < 18;
    case TIME_OF_DAY.EVENING:
      return eventHour >= 18 || eventHour < 6;
    default:
      return true;
  }
};

// Check if event is happening NOW (next 2 hours)
const eventIsNow = (event) => {
  const now = new Date();
  const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  
  if (!eventOccursOnDate(event, now)) return false;
  
  if (event.timeSlots?.length > 0) {
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const twoHoursTime = twoHoursLater.getHours() * 60 + twoHoursLater.getMinutes();
    
    return event.timeSlots.some(slot => {
      const [startH, startM] = slot.start.split(':').map(Number);
      const slotStart = startH * 60 + startM;
      return slotStart >= currentTime && slotStart <= twoHoursTime;
    });
  }
  
  if (event.eventStartsAt) {
    const eventTime = new Date(event.eventStartsAt);
    return eventTime >= now && eventTime <= twoHoursLater;
  }
  
  return true;
};

// Format helpers
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(dtLocale(), { month: 'short', day: 'numeric' });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString(dtLocale(), { hour: 'numeric', minute: '2-digit' });
};

const formatPrice = (from, to, currency) => {
  if (from === 0 && to === 0) return 'Free';
  if (from === to) return `${currency} ${from}`;
  if (to === 0) return `From ${currency} ${from}`;
  return `${currency} ${from}-${to}`;
};

const getMarkerSize = (zoom, isSelected) => {
  let baseSize = zoom <= 10 ? 24 + (zoom - 8) * 2 : zoom <= 13 ? 28 + (zoom - 10) * 4 : 40 + (zoom - 13) * 3;
  baseSize = Math.max(20, Math.min(52, baseSize));
  return isSelected ? baseSize * 1.3 : baseSize;
};

// Calculate bounds - drawerHeightPercent is the % of screen hidden by drawer
const calculateBounds = (events, drawerHeightPercent = 0) => {
  if (!events || events.length === 0) return null;
  
  // Filter for events with valid numeric coordinates (not null, undefined, or NaN)
  const validEvents = events.filter(e => 
    typeof e.longitude === 'number' && 
    typeof e.latitude === 'number' && 
    !isNaN(e.longitude) && 
    !isNaN(e.latitude) &&
    isFinite(e.longitude) &&
    isFinite(e.latitude)
  );
  
  if (validEvents.length === 0) return null;
  
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  validEvents.forEach(event => {
    minLng = Math.min(minLng, event.longitude);
    maxLng = Math.max(maxLng, event.longitude);
    minLat = Math.min(minLat, event.latitude);
    maxLat = Math.max(maxLat, event.latitude);
  });
  
  // Safety check - if bounds are still invalid, return null
  if (!isFinite(minLng) || !isFinite(maxLng) || !isFinite(minLat) || !isFinite(maxLat)) {
    return null;
  }
  
  const lngPadding = (maxLng - minLng) * 0.15 || 0.01;
  let latPadding = (maxLat - minLat) * 0.15 || 0.01;
  
  return { 
    minLng: minLng - lngPadding, 
    maxLng: maxLng + lngPadding, 
    minLat: minLat - latPadding, 
    maxLat: maxLat + latPadding, 
    centerLng: (minLng + maxLng) / 2, 
    centerLat: (minLat + maxLat) / 2 
  };
};

// ==================== COMPONENTS ====================

const EventMarker = memo(({ event, onClick, isSelected, zoom }) => {
  const config = getEventTypeConfig(event.eventTypeSlug);
  const size = getMarkerSize(zoom, isSelected);
  const color = config.color || '#ea580c';
  
  return (
    <motion.div 
      initial={{ scale: 0.5, opacity: 0 }} 
      animate={{ scale: 1, opacity: 1 }}
      onClick={(e) => { e.stopPropagation(); onClick(event); }}
      style={{ 
        width: `${size}px`, 
        height: `${size}px`, 
        borderRadius: '50%', 
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`, 
        border: isSelected ? '3px solid white' : '2px solid white', 
        boxShadow: isSelected ? `0 0 20px ${color}88` : '0 2px 8px rgba(0,0,0,0.3)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        cursor: 'pointer', 
        fontSize: `${Math.max(12, size * 0.45)}px`, 
        transform: 'translate(-50%, -50%)', 
        zIndex: isSelected ? 100 : 1,
        pointerEvents: 'auto'
      }}
      data-testid={`event-marker-${event.id}`}
    >
      {config.icon || '📍'}
    </motion.div>
  );
});
EventMarker.displayName = 'EventMarker';

// Event Card - Beautiful with Photo (works for both desktop and mobile)
const EventCard = memo(({ event, onClick, isSelected, isDesktop }) => {
  const navigate = useNavigate();
  const config = getEventTypeConfig(event.eventTypeSlug);
  const color = config.color || '#ea580c';
  const eventTypeLabel = getEventTypeDisplayName(event.eventTypeSlug);
  
  const handleVenueClick = (e) => {
    e.stopPropagation();
    if (event.venueId) {
      navigate(`/venue/${event.venueId}`);
    }
  };
  
  return (
    <motion.div 
      whileTap={{ scale: 0.98 }} 
      onClick={() => onClick(event)}
      style={{ 
        width: '100%',
        background: isSelected ? `linear-gradient(135deg, ${color}15, ${color}08)` : 'white', 
        borderRadius: '16px', 
        overflow: 'hidden',
        cursor: 'pointer', 
        border: isSelected ? `2px solid ${color}` : '1px solid #e5e7eb', 
        boxShadow: isSelected ? `0 6px 20px ${color}20` : '0 2px 10px rgba(0,0,0,0.06)', 
        marginBottom: '12px',
        transition: 'all 0.2s ease'
      }}
      data-testid={`event-card-${event.id}`}
    >
      {/* Compact Header with gradient */}
      <div style={{ 
        position: 'relative', 
        height: isDesktop ? '100px' : '80px', 
        background: `linear-gradient(135deg, ${color}25, ${color}10)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span style={{ fontSize: isDesktop ? '40px' : '32px' }}>{config.icon || '📅'}</span>
        
        {/* Event Type Badge - Top Right */}
        <div style={{ 
          position: 'absolute', 
          top: '8px', 
          right: '8px',
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px',
          padding: '3px 8px',
          borderRadius: '12px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          border: `1px solid ${color}30`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
        }}>
          <span style={{ fontSize: '10px' }}>{config.icon || '📅'}</span>
          <span style={{ fontSize: '10px', color: color, fontWeight: '700' }}>{eventTypeLabel}</span>
        </div>
        
        {/* Date Badge - Top Left */}
        <div style={{ 
          position: 'absolute', 
          top: '8px', 
          left: '8px',
          padding: '4px 8px',
          borderRadius: '8px',
          backgroundColor: 'rgba(0,0,0,0.75)',
          color: 'white'
        }}>
          <div style={{ fontSize: '10px', fontWeight: '700' }}>{event.date}</div>
          <div style={{ fontSize: '9px', opacity: 0.9 }}>{event.time}</div>
        </div>
      </div>
      
      {/* Content */}
      <div style={{ padding: '12px' }}>
        <h3 style={{ 
          fontSize: '14px', 
          fontWeight: '700', 
          color: '#111827', 
          margin: 0, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          lineHeight: 1.3
        }}>
          {event.title}
        </h3>
        
        {/* Venue - Clickable */}
        <button 
          onClick={handleVenueClick}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px', 
            marginTop: '8px',
            padding: '5px 8px',
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left'
          }}
        >
          <MapPinIcon style={{ width: '12px', height: '12px', color: '#ea580c', flexShrink: 0 }} />
          <span style={{ 
            fontSize: '11px', 
            color: '#374151', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            fontWeight: '500',
            flex: 1
          }}>
            {event.venue}
          </span>
          <ChevronRightIcon style={{ width: '10px', height: '10px', color: '#9ca3af', flexShrink: 0 }} />
        </button>
        
        {/* Price Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <span style={{ 
            fontSize: '12px', 
            fontWeight: '700', 
            color: event.isFree ? '#10b981' : '#111827',
            padding: '3px 6px',
            backgroundColor: event.isFree ? '#d1fae5' : '#f3f4f6',
            borderRadius: '4px'
          }}>
            {event.price}
          </span>
          <span style={{ fontSize: '7px', color: '#9ca3af', fontFamily: 'monospace' }}>{event.id}</span>
        </div>
      </div>
    </motion.div>
  );
});
EventCard.displayName = 'EventCard';

// Filter Chip with count
const FilterChip = ({ label, icon, isSelected, onClick, color = '#6b7280', count, compact = false }) => (
  <motion.button 
    whileTap={{ scale: 0.97 }} 
    onClick={onClick}
    style={{ 
      padding: compact ? '5px 10px' : '8px 14px', 
      borderRadius: '16px', 
      border: isSelected ? `2px solid ${color}` : '1px solid #e5e7eb', 
      backgroundColor: isSelected ? `${color}15` : 'white', 
      color: isSelected ? color : '#374151', 
      fontSize: compact ? '11px' : '13px', 
      fontWeight: '600', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '4px', 
      whiteSpace: 'nowrap', 
      flexShrink: 0 
    }}
  >
    {icon && <span style={{ fontSize: compact ? '12px' : '16px' }}>{icon}</span>}
    {label}
    {count !== undefined && count > 0 && (
      <span style={{ 
        backgroundColor: isSelected ? color : '#e5e7eb', 
        color: isSelected ? 'white' : '#374151',
        padding: '1px 5px', 
        borderRadius: '8px', 
        fontSize: '10px',
        fontWeight: '700',
        minWidth: '18px',
        textAlign: 'center'
      }}>
        {count}
      </span>
    )}
  </motion.button>
);

// Compact Time of Day Selector for Mobile
const TimeOfDaySelector = ({ selectedTime, onTimeChange, compact = false }) => {
  const timeOptions = [
    { key: TIME_OF_DAY.ALL, label: 'All', icon: '🌍' },
    { key: TIME_OF_DAY.MORNING, label: 'AM', icon: '☀️' },
    { key: TIME_OF_DAY.AFTERNOON, label: 'PM', icon: '🌅' },
    { key: TIME_OF_DAY.EVENING, label: 'Night', icon: '🌙' }
  ];
  
  return (
    <div style={{ 
      display: 'flex', 
      gap: '4px', 
      padding: compact ? '6px 10px' : '8px 12px',
      backgroundColor: '#fafafa'
    }}>
      {timeOptions.map(option => (
        <button
          key={option.key}
          onClick={() => onTimeChange(option.key)}
          style={{
            flex: 1,
            padding: compact ? '4px 2px' : '6px 4px',
            borderRadius: '8px',
            border: selectedTime === option.key ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
            background: selectedTime === option.key ? '#8b5cf6' : 'white',
            color: selectedTime === option.key ? 'white' : '#374151',
            fontSize: compact ? '10px' : '11px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1px'
          }}
        >
          <span style={{ fontSize: compact ? '12px' : '14px' }}>{option.icon}</span>
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
};

// Compact Date Range Selector
const DateRangeSelector = ({ selectedRange, selectedDates, onRangeChange, onDateClick, compact = false }) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  
  const dates = useMemo(() => {
    const result = [];
    for (let i = -7; i <= 14; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      result.push(d);
    }
    return result;
  }, [today]);
  
  const carouselRef = useRef(null);
  const todayRef = useRef(null);
  
  useEffect(() => {
    if (todayRef.current && carouselRef.current) {
      const container = carouselRef.current;
      const todayEl = todayRef.current;
      const containerWidth = container.offsetWidth;
      const todayLeft = todayEl.offsetLeft;
      const todayWidth = todayEl.offsetWidth;
      container.scrollLeft = todayLeft - containerWidth / 2 + todayWidth / 2;
    }
  }, []);
  
  const isDateSelected = (date) => selectedDates.some(d => d.getTime() === date.getTime());
  const isPastDate = (date) => date.getTime() < today.getTime();
  
  return (
    <div style={{ padding: compact ? '6px 8px' : '10px 12px', backgroundColor: '#fafafa' }}>
      {/* Quick Range Buttons - More compact */}
      <div style={{ 
        display: 'flex', 
        gap: compact ? '4px' : '6px', 
        marginBottom: compact ? '6px' : '10px',
        overflowX: 'auto',
        scrollbarWidth: 'none'
      }}>
        {[
          { key: DATE_RANGES.LAST_7, label: 'Last 7', color: '#6366f1' },
          { key: DATE_RANGES.NOW, label: 'NOW', color: '#ea580c', icon: true },
          { key: DATE_RANGES.NEXT_7, label: 'Next 7', color: '#ea580c' },
          { key: DATE_RANGES.NEXT_14, label: 'Next 14', color: '#ea580c' },
          { key: DATE_RANGES.CUSTOM, label: 'Custom', color: '#6366f1' }
        ].map(btn => (
          <button 
            key={btn.key}
            onClick={() => onRangeChange(btn.key)}
            style={{ 
              padding: compact ? '6px 10px' : '8px 14px', 
              borderRadius: '8px', 
              border: selectedRange === btn.key ? `2px solid ${btn.color}` : '1px solid #e5e7eb', 
              background: selectedRange === btn.key ? btn.color : 'white', 
              color: selectedRange === btn.key ? 'white' : '#374151', 
              fontSize: compact ? '11px' : '12px', 
              fontWeight: '600', 
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {btn.icon && <ClockIcon style={{ width: 12, height: 12 }} />}
            {btn.label}
          </button>
        ))}
      </div>
      
      {/* Date Carousel - More compact squares */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <button 
          onClick={() => { if (carouselRef.current) carouselRef.current.scrollBy({ left: -150, behavior: 'smooth' }); }}
          style={{ padding: '4px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', display: 'flex', flexShrink: 0 }}
        >
          <ChevronLeftIcon style={{ width: 14, height: 14, color: '#374151' }} />
        </button>
        
        <div 
          ref={carouselRef} 
          style={{ 
            flex: 1, 
            display: 'flex', 
            gap: '4px', 
            overflowX: 'auto', 
            scrollbarWidth: 'none', 
            padding: '2px 0',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {dates.map((date, index) => {
            const isSelected = isDateSelected(date);
            const isToday = date.toDateString() === today.toDateString();
            const isPast = isPastDate(date);
            
            return (
              <button 
                key={index} 
                ref={isToday ? todayRef : null}
                onClick={() => onDateClick(date)}
                style={{ 
                  minWidth: compact ? '40px' : '46px', 
                  height: compact ? '48px' : '54px',
                  padding: '4px 2px', 
                  borderRadius: '8px', 
                  border: isToday 
                    ? (isSelected ? '2px solid #3b82f6' : '2px solid #3b82f6')
                    : (isSelected ? '2px solid #ea580c' : '1px solid #e5e7eb'),
                  background: isSelected ? '#ea580c' : isToday ? '#dbeafe' : isPast ? '#f9fafb' : 'white', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '1px', 
                  flexShrink: 0,
                  opacity: isPast && !isSelected ? 0.6 : 1
                }}
                data-testid={`date-${date.toISOString().split('T')[0]}`}
              >
                <span style={{ 
                  fontSize: '9px', 
                  fontWeight: '600', 
                  color: isSelected ? 'white' : isToday ? '#3b82f6' : isPast ? '#9ca3af' : '#6b7280', 
                  textTransform: 'uppercase' 
                }}>
                  {date.toLocaleDateString(dtLocale(), { weekday: 'short' }).slice(0, 2)}
                </span>
                <span style={{ 
                  fontSize: compact ? '14px' : '15px', 
                  fontWeight: '700', 
                  color: isSelected ? 'white' : isToday ? '#1d4ed8' : isPast ? '#6b7280' : '#111827', 
                  lineHeight: 1 
                }}>
                  {date.getDate()}
                </span>
                <span style={{ 
                  fontSize: '8px', 
                  color: isSelected ? 'rgba(255,255,255,0.8)' : '#9ca3af' 
                }}>
                  {date.toLocaleDateString(dtLocale(), { month: 'short' }).slice(0, 3)}
                </span>
              </button>
            );
          })}
        </div>
        
        <button 
          onClick={() => { if (carouselRef.current) carouselRef.current.scrollBy({ left: 150, behavior: 'smooth' }); }}
          style={{ padding: '4px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', display: 'flex', flexShrink: 0 }}
        >
          <ChevronRightIcon style={{ width: 14, height: 14, color: '#374151' }} />
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const EventsMapPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const isDesktop = useIsDesktop();
  const eventListRef = useRef(null);
  
  const [viewState, setViewState] = useState({ 
    longitude: REGION_CONFIG.mapCenter.longitude, 
    latitude: REGION_CONFIG.mapCenter.latitude, 
    zoom: REGION_CONFIG.mapCenter.zoom 
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedETC1, setSelectedETC1] = useState('');
  const [selectedETC2, setSelectedETC2] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState(TIME_OF_DAY.ALL);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  
  // Date range state - default to Next 7 Days
  const [selectedRange, setSelectedRange] = useState(DATE_RANGES.NEXT_7);
  const [selectedDates, setSelectedDates] = useState(() => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  });
  
  // Handle range change
  const handleRangeChange = useCallback((range) => {
    setSelectedRange(range);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (range === DATE_RANGES.LAST_7) {
      const dates = [];
      for (let i = -7; i <= 0; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        dates.push(d);
      }
      setSelectedDates(dates);
    } else if (range === DATE_RANGES.NOW) {
      setSelectedDates([today]);
    } else if (range === DATE_RANGES.NEXT_7) {
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        dates.push(d);
      }
      setSelectedDates(dates);
    } else if (range === DATE_RANGES.NEXT_14) {
      const dates = [];
      for (let i = 0; i < 14; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        dates.push(d);
      }
      setSelectedDates(dates);
    }
  }, []);
  
  // Handle individual date click
  const handleDateClick = useCallback((date) => {
    setSelectedRange(DATE_RANGES.CUSTOM);
    setSelectedDates(prev => {
      const exists = prev.some(d => d.getTime() === date.getTime());
      if (exists) {
        const newDates = prev.filter(d => d.getTime() !== date.getTime());
        return newDates.length > 0 ? newDates : [date];
      } else {
        return [...prev, date].sort((a, b) => a.getTime() - b.getTime());
      }
    });
  }, []);
  
  // Handle scroll on event list to collapse filters
  const handleEventListScroll = useCallback((e) => {
    const scrollTop = e.target.scrollTop;
    if (scrollTop > 50 && !filtersCollapsed) {
      setFiltersCollapsed(true);
    } else if (scrollTop < 10 && filtersCollapsed) {
      setFiltersCollapsed(false);
    }
  }, [filtersCollapsed]);
  
  // Fetch events
  const { data, isLoading, error } = useQuery({
    queryKey: ['events', 'th'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/events?limit=500&upcoming=false`, {
        headers: { 'x-region': 'th', 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to fetch events');
      return response.json();
    },
    staleTime: 1000 * 60 * 5,
    retry: 2
  });
  
  // Transform events
  const events = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map(event => {
      const priceFrom = event.eventPriceFrom || 0;
      const priceMax = event.eventPriceMax || 0;
      const currency = event.eventCurrency || 'THB';
      const coords = event.venueId?.geometry?.coordinates || event.venueId?.location?.coordinates;
      
      return {
        id: event._id,
        title: event.eventName || 'Unnamed Event',
        venue: event.venueId?.name || 'Unknown Venue',
        venueId: event.venueId?._id,
        date: formatDate(event.eventStartsAt),
        time: formatTime(event.eventStartsAt),
        price: formatPrice(priceFrom, priceMax, currency),
        image: event.images?.[0] || event.imageUrl || null,
        eventType: event.eventType || '',
        eventCategory: event.eventCategory || '',
        eventTypeSlug: event.eventTypeSlug || '',
        description: event.description || '',
        isFree: priceFrom === 0 && priceMax === 0,
        longitude: coords ? coords[0] : null,
        latitude: coords ? coords[1] : null,
        eventStartsAt: event.eventStartsAt,
        eventEndsAt: event.eventEndsAt,
        scheduleMode: event.scheduleMode,
        eventRecurrence: event.eventRecurrence,
        seriesStartDate: event.seriesStartDate,
        seriesEndDate: event.seriesEndDate,
        seriesIsActive: event.seriesIsActive,
        recurrenceFreq: event.recurrenceFreq,
        recurrenceInterval: event.recurrenceInterval,
        byWeekday: event.byWeekday,
        byMonthday: event.byMonthday,
        timeSlots: event.timeSlots,
        exceptionsCancelledDates: event.exceptionsCancelledDates,
        isActive: event.isActive,
        daysOfWeek: event.daysOfWeek
      };
    });
  }, [data]);
  
  // Filter events
  const filteredEvents = useMemo(() => {
    let result = events;
    
    if (selectedRange === DATE_RANGES.NOW) {
      result = result.filter(event => eventIsNow(event));
    } else if (selectedDates.length > 0) {
      result = result.filter(event => selectedDates.some(date => eventOccursOnDate(event, date)));
    }
    
    result = result.filter(event => eventMatchesTimeOfDay(event, selectedTimeOfDay));
    
    if (selectedETC1) result = result.filter(e => e.eventType === selectedETC1);
    if (selectedETC2) result = result.filter(e => e.eventCategory === selectedETC2);
    if (selectedEventType) result = result.filter(e => e.eventTypeSlug === selectedEventType);
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(e => {
        const typeLabel = getEventTypeConfig(e.eventTypeSlug).label.toLowerCase();
        return e.title.toLowerCase().includes(query) || e.venue.toLowerCase().includes(query) || typeLabel.includes(query);
      });
    }
    
    return result;
  }, [events, selectedRange, selectedDates, selectedTimeOfDay, selectedETC1, selectedETC2, selectedEventType, searchQuery]);
  
  // Calculate counts for filters
  const dateFilteredEvents = useMemo(() => {
    let result = events;
    if (selectedRange === DATE_RANGES.NOW) {
      result = result.filter(event => eventIsNow(event));
    } else if (selectedDates.length > 0) {
      result = result.filter(event => selectedDates.some(date => eventOccursOnDate(event, date)));
    }
    result = result.filter(event => eventMatchesTimeOfDay(event, selectedTimeOfDay));
    return result;
  }, [events, selectedRange, selectedDates, selectedTimeOfDay]);
  
  const totalEventCount = dateFilteredEvents.length;
  
  const availableETC1s = useMemo(() => {
    const etc1Counts = {};
    dateFilteredEvents.forEach(e => {
      if (e.eventType) etc1Counts[e.eventType] = (etc1Counts[e.eventType] || 0) + 1;
    });
    return Object.entries(ETC1_CONFIG)
      .filter(([key]) => etc1Counts[key])
      // CON-1613 — label MUST come from the getter (override → txLabel); the raw
      // ETC1_CONFIG label is English-only and this is THE map filter chip row.
      .map(([key, config]) => ({ key, ...config, label: getETC1Config(key).label, count: etc1Counts[key] || 0 }));
  }, [dateFilteredEvents]);
  
  const availableETC2s = useMemo(() => {
    if (!selectedETC1) return [];
    const etc2Counts = {};
    dateFilteredEvents.filter(e => e.eventType === selectedETC1).forEach(e => {
      if (e.eventCategory) etc2Counts[e.eventCategory] = (etc2Counts[e.eventCategory] || 0) + 1;
    });
    return Object.entries(ETC2_CONFIG)
      .filter(([key, config]) => config.parent === selectedETC1 && etc2Counts[key])
      .map(([key, config]) => ({ key, ...config, label: getETC2Config(key).label, count: etc2Counts[key] || 0 }));
  }, [selectedETC1, dateFilteredEvents]);
  
  const availableEventTypes = useMemo(() => {
    if (!selectedETC2) return [];
    const typeCounts = {};
    dateFilteredEvents.filter(e => e.eventCategory === selectedETC2).forEach(e => {
      if (e.eventTypeSlug) typeCounts[e.eventTypeSlug] = (typeCounts[e.eventTypeSlug] || 0) + 1;
    });
    return Object.entries(EVENT_TYPE_SLUGS)
      .filter(([key, config]) => config.parent === selectedETC2 && typeCounts[key])
      .map(([key, config]) => ({ key, ...config, count: typeCounts[key] || 0 }));
  }, [selectedETC2, dateFilteredEvents]);
  
  const mappableEvents = useMemo(() => filteredEvents.filter(e => 
    typeof e.longitude === 'number' && 
    typeof e.latitude === 'number' && 
    !isNaN(e.longitude) && 
    !isNaN(e.latitude) &&
    isFinite(e.longitude) &&
    isFinite(e.latitude)
  ), [filteredEvents]);
  const hasActiveFilters = selectedETC1 || selectedETC2 || selectedEventType || searchQuery;
  
  const handleClearFilters = useCallback(() => {
    setSelectedETC1('');
    setSelectedETC2('');
    setSelectedEventType('');
    setSearchQuery('');
  }, []);
  
  // Scroll event list to show selected event at top
  const scrollToSelectedEvent = useCallback((eventId) => {
    if (!eventListRef.current) return;
    
    // Find the event card element
    const eventCard = eventListRef.current.querySelector(`[data-testid="event-card-${eventId}"]`);
    if (eventCard) {
      // Scroll it into view at the top
      eventCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  const handleEventClick = useCallback((event) => {
    setSelectedEvent(event);
    
    if (event.longitude && event.latitude && mapRef.current) {
      if (isDesktop) {
        // Desktop: center normally
        mapRef.current.flyTo({ center: [event.longitude, event.latitude], zoom: 15, duration: 1000 });
      } else {
        // Mobile: The visible map is only the top ~35% of the screen (65% is drawer)
        // We need to pan the map so the marker appears in the VISIBLE portion (top 35%)
        // 
        // Strategy: Use Mapbox's built-in padding for flyTo
        // The padding tells Mapbox where the "safe" viewable area is
        // With bottom padding = 65% of screen height, Mapbox will center in the remaining area
        
        const screenHeight = window.innerHeight;
        const drawerHeight = screenHeight * 0.65; // 65% drawer
        
        mapRef.current.flyTo({ 
          center: [event.longitude, event.latitude], 
          zoom: 15, 
          duration: 1000,
          padding: { top: 60, bottom: drawerHeight, left: 20, right: 20 }
        });
      }
    }
    
    // Scroll event list to show this event at top
    setTimeout(() => scrollToSelectedEvent(event.id), 100);
  }, [isDesktop, scrollToSelectedEvent]);
  
  // Fit bounds - for mobile, use large bottom padding to account for drawer
  useEffect(() => {
    if (!mapRef.current || mappableEvents.length === 0) return;
    const bounds = calculateBounds(mappableEvents);
    if (!bounds) return;
    
    // Mobile: drawer takes ~65% of screen, so we need massive bottom padding
    // Desktop: normal padding
    const padding = isDesktop 
      ? 50 
      : { top: 80, bottom: window.innerHeight * 0.7, left: 20, right: 20 };
    
    if (mappableEvents.length === 1) {
      if (isDesktop) {
        mapRef.current.flyTo({ center: [bounds.centerLng, bounds.centerLat], zoom: 14, duration: 1000 });
      } else {
        // Mobile single event: use padding to ensure marker is in visible area
        const screenHeight = window.innerHeight;
        const drawerHeight = screenHeight * 0.65;
        
        mapRef.current.flyTo({ 
          center: [bounds.centerLng, bounds.centerLat], 
          zoom: 14, 
          duration: 1000,
          padding: { top: 60, bottom: drawerHeight, left: 20, right: 20 }
        });
      }
    } else {
      mapRef.current.fitBounds(
        [[bounds.minLng, bounds.minLat], [bounds.maxLng, bounds.maxLat]], 
        { padding, duration: 1000, maxZoom: 14 }
      );
    }
  }, [mappableEvents, isDesktop]);
  
  useEffect(() => { setSelectedETC2(''); setSelectedEventType(''); }, [selectedETC1]);
  useEffect(() => { setSelectedEventType(''); }, [selectedETC2]);
  
  // ==================== DESKTOP RENDER ====================
  if (isDesktop) {
    return (
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Left Panel */}
        <div style={{ width: '35%', minWidth: '380px', maxWidth: '500px', height: '100%', display: 'flex', flexDirection: 'column', borderRight: '1px solid #e5e7eb', backgroundColor: 'white' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => navigate(-1)} style={{ padding: '8px', borderRadius: '10px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex' }}>
              <ArrowLeftIcon style={{ width: 20, height: 20, color: '#374151' }} />
            </button>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>{t('events.title')}</h1>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{filteredEvents.length} {t('events.title').toLowerCase()} • {mappableEvents.length} {t('events.onMap')}</p>
            </div>
          </div>
          
          {/* Search Bar - Always visible */}
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ backgroundColor: '#f3f4f6', borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #e5e7eb' }}>
              <span style={{ color: '#9ca3af', fontSize: '16px' }}>🔍</span>
              <input
                type="text"
                placeholder={t('events.searchPlaceholderFull')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '14px', backgroundColor: 'transparent', color: '#111827' }}
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  style={{ padding: '2px 6px', background: '#e5e7eb', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', color: '#6b7280' }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          <DateRangeSelector selectedRange={selectedRange} selectedDates={selectedDates} onRangeChange={handleRangeChange} onDateClick={handleDateClick} />
          <TimeOfDaySelector selectedTime={selectedTimeOfDay} onTimeChange={setSelectedTimeOfDay} />
          
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={() => setShowFilters(!showFilters)} style={{ padding: '6px 12px', borderRadius: '8px', border: hasActiveFilters ? '2px solid #ea580c' : '1px solid #e5e7eb', background: hasActiveFilters ? '#fff7ed' : 'white', color: hasActiveFilters ? '#ea580c' : '#374151', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FunnelIcon style={{ width: 14, height: 14 }} />
              {showFilters ? t('filters.hide') : t('filters.title')}
            </button>
            {hasActiveFilters && <button onClick={handleClearFilters} style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>{t('filters.clear')}</button>}
          </div>
          
          <AnimatePresence>
            {showFilters && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: 'hidden', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ padding: '10px 16px', backgroundColor: '#f9fafb' }}>
                  <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>🔍</span>
                    <input type="text" placeholder={t('common.search')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', backgroundColor: 'transparent' }} />
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' }}>{t('filters.category')}</div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <FilterChip label={t('filters.all')} icon="🌍" isSelected={!selectedETC1} onClick={() => setSelectedETC1('')} color="#6366f1" count={totalEventCount} />
                      {availableETC1s.map(etc1 => <FilterChip key={etc1.key} label={etc1.label} icon={etc1.icon} isSelected={selectedETC1 === etc1.key} onClick={() => setSelectedETC1(selectedETC1 === etc1.key ? '' : etc1.key)} color={etc1.color} count={etc1.count} />)}
                    </div>
                  </div>
                  {availableETC2s.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '10px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' }}>{t('filters.subcategory')}</div>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {availableETC2s.map(etc2 => <FilterChip key={etc2.key} label={etc2.label} icon={etc2.icon} isSelected={selectedETC2 === etc2.key} onClick={() => setSelectedETC2(selectedETC2 === etc2.key ? '' : etc2.key)} color={etc2.color} count={etc2.count} />)}
                      </div>
                    </div>
                  )}
                  {availableEventTypes.length > 0 && (
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' }}>{t('filters.type')}</div>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {availableEventTypes.map(et => <FilterChip key={et.key} label={et.label} icon={et.icon} isSelected={selectedEventType === et.key} onClick={() => setSelectedEventType(selectedEventType === et.key ? '' : et.key)} color={et.color} count={et.count} />)}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div ref={eventListRef} onScroll={handleEventListScroll} style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                <div style={{ width: '36px', height: '36px', border: '3px solid #f3f4f6', borderTop: '3px solid #ea580c', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}><span style={{ fontSize: '28px' }}>⚠️</span><p>{t('events.errorLoading')}</p></div>
            ) : filteredEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}><span style={{ fontSize: '28px' }}>📅</span><p style={{ fontWeight: '600' }}>{t('events.noEvents')}</p></div>
            ) : (
              filteredEvents.map(event => <EventCard key={event.id} event={event} onClick={handleEventClick} isSelected={selectedEvent?.id === event.id} isDesktop={true} />)
            )}
          </div>
        </div>
        
        {/* Map */}
        <div style={{ flex: 1, position: 'relative' }}>
          <MapGL ref={mapRef} {...viewState} onMove={evt => setViewState(evt.viewState)} mapStyle="mapbox://styles/mapbox/streets-v12" mapboxAccessToken={MAPBOX_TOKEN} style={{ width: '100%', height: '100%' }}>
            <NavigationControl position="top-right" />
            <GeolocateControl position="top-right" />
            {mappableEvents.map(event => (
              <Marker key={event.id} longitude={event.longitude} latitude={event.latitude} anchor="center" style={{ cursor: 'pointer' }}>
                <EventMarker event={event} onClick={handleEventClick} isSelected={selectedEvent?.id === event.id} zoom={viewState.zoom} />
              </Marker>
            ))}
          </MapGL>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  
  // ==================== MOBILE RENDER ====================
  return (
    <div style={{ height: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Header variant="transparent" showBack title={t('events.title')} />
      
      {/* Map - Full screen behind drawer */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
        <MapGL ref={mapRef} {...viewState} onMove={evt => setViewState(evt.viewState)} mapStyle="mapbox://styles/mapbox/streets-v12" mapboxAccessToken={MAPBOX_TOKEN} style={{ width: '100%', height: '100%' }}>
          <NavigationControl position="top-right" style={{ marginTop: '60px' }} />
          <GeolocateControl position="top-right" style={{ marginTop: '100px' }} />
          {mappableEvents.map(event => (
            <Marker key={event.id} longitude={event.longitude} latitude={event.latitude} anchor="center" style={{ cursor: 'pointer' }}>
              <EventMarker event={event} onClick={handleEventClick} isSelected={selectedEvent?.id === event.id} zoom={viewState.zoom} />
            </Marker>
          ))}
        </MapGL>
      </div>
      
      {/* Bottom Drawer - 65% of screen */}
      <motion.div 
        initial={{ y: 100 }} 
        animate={{ y: 0 }}
        style={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          backgroundColor: 'white', 
          borderTopLeftRadius: '20px', 
          borderTopRightRadius: '20px', 
          boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', 
          zIndex: 100, 
          height: '65vh',
          display: 'flex', 
          flexDirection: 'column' 
        }}
      >
        {/* Drag Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px 0' }}>
          <div style={{ width: '40px', height: '4px', backgroundColor: '#d1d5db', borderRadius: '2px' }} />
        </div>
        
        {/* Search Bar - Mobile */}
        <div style={{ padding: '6px 10px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #e5e7eb' }}>
            <span style={{ color: '#9ca3af', fontSize: '14px' }}>🔍</span>
            <input
              type="text"
              placeholder={t('events.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', backgroundColor: 'transparent', color: '#111827' }}
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                style={{ padding: '2px 6px', background: '#e5e7eb', border: 'none', borderRadius: '4px', fontSize: '10px', cursor: 'pointer', color: '#6b7280' }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Collapsible Header Section */}
        <AnimatePresence>
          {!filtersCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              style={{ overflow: 'hidden' }}
            >
              <DateRangeSelector selectedRange={selectedRange} selectedDates={selectedDates} onRangeChange={handleRangeChange} onDateClick={handleDateClick} compact={true} />
              <TimeOfDaySelector selectedTime={selectedTimeOfDay} onTimeChange={setSelectedTimeOfDay} compact={true} />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Events Header Bar */}
        <div 
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          style={{ 
            padding: '8px 12px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            borderBottom: '1px solid #e5e7eb',
            cursor: 'pointer',
            backgroundColor: '#fafafa'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {filtersCollapsed ? <ChevronDownIcon style={{ width: 16, height: 16, color: '#6b7280' }} /> : <ChevronUpIcon style={{ width: 16, height: 16, color: '#6b7280' }} />}
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{t('events.title')}</span>
            <span style={{ backgroundColor: '#ea580c', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>{filteredEvents.length}</span>
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); setShowFilters(!showFilters); }}
            style={{ padding: '5px 10px', borderRadius: '6px', border: hasActiveFilters ? '2px solid #ea580c' : '1px solid #e5e7eb', backgroundColor: hasActiveFilters ? '#fff7ed' : 'white', color: hasActiveFilters ? '#ea580c' : '#374151', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <FunnelIcon style={{ width: 12, height: 12 }} />
            {t('filters.title')}
          </button>
        </div>
        
        {/* Category Filters - Always visible but collapsible */}
        {showFilters && (
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', maxHeight: '100px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none' }}>
              <FilterChip label={t('filters.all')} icon="🌍" isSelected={!selectedETC1} onClick={() => setSelectedETC1('')} color="#6366f1" count={totalEventCount} compact={true} />
              {availableETC1s.map(etc1 => <FilterChip key={etc1.key} label={etc1.label} icon={etc1.icon} isSelected={selectedETC1 === etc1.key} onClick={() => setSelectedETC1(selectedETC1 === etc1.key ? '' : etc1.key)} color={etc1.color} count={etc1.count} compact={true} />)}
            </div>
            {availableETC2s.length > 0 && (
              <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingTop: '6px', scrollbarWidth: 'none' }}>
                {availableETC2s.map(etc2 => <FilterChip key={etc2.key} label={etc2.label} icon={etc2.icon} isSelected={selectedETC2 === etc2.key} onClick={() => setSelectedETC2(selectedETC2 === etc2.key ? '' : etc2.key)} color={etc2.color} count={etc2.count} compact={true} />)}
              </div>
            )}
          </div>
        )}
        
        {/* Event List - VERTICAL scrollable (not horizontal) */}
        <div 
          ref={eventListRef}
          onScroll={handleEventListScroll}
          style={{ 
            flex: 1, 
            padding: '10px', 
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <div style={{ width: '32px', height: '32px', border: '3px solid #f3f4f6', borderTop: '3px solid #ea580c', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px', textAlign: 'center' }}>
              <span style={{ fontSize: '28px', marginBottom: '8px' }}>📅</span>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: 0 }}>{t('events.noEvents')}</p>
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0 0' }}>{t('events.adjustFilters')}</p>
            </div>
          ) : (
            filteredEvents.map(event => (
              <EventCard 
                key={event.id} 
                event={event} 
                onClick={handleEventClick} 
                isSelected={selectedEvent?.id === event.id} 
                isDesktop={false} 
              />
            ))
          )}
        </div>
      </motion.div>
      
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default EventsMapPage;
